/**
 * Gerätelokale Konfiguration (localStorage): Quellen mit stabiler source.id,
 * damit nach Reload/Wiederbeitritt ohne Neukonfiguration dieselbe Quelle
 * weiterläuft (Doc 13 §3.1). Reine Funktionen + injizierbarer Storage → testbar.
 */
import type { CaptureMode, Processing, SourceConfig, SourceKind } from '@voice/protocol';
import { defaultProcessing } from '@voice/protocol/runtime';

export const STORAGE_KEY = 'bentimento.voiceJoin.v1';

export interface StoredSource {
  id: string;
  /** lapel = Ansteckmikro, room = Raum, remote = Konferenz-Teilnehmer am eigenen Laptop (Doc 15 §1.1) */
  kind: Extract<SourceKind, 'lapel' | 'room' | 'remote'>;
  /** Sprechername (lapel/remote) bzw. Quellenname (room) */
  label: string;
  /**
   * Eingang: `device` = Mikrofon per getUserMedia (deviceId/Kanal), `display` =
   * Tab-/Fenster-Audio per getDisplayMedia (Preset Konferenz, nur mit `room`,
   * Doc 15 §1.2). Der Display-Stream selbst ist nicht speicherbar; nach einem
   * Reload muss der Tab neu gewählt werden.
   */
  input: SourceInput;
  participationId: string | null;
  deviceId: string | null;
  deviceLabel: string | null;
  /** null = Downmix/Mono; 0 = links, 1 = rechts … */
  channel: number | null;
  channels: number | null;
  mode: CaptureMode;
  processing: Processing;
  /**
   * optional: Mindestdauer zusammenhängender Sprache, bevor die App-VAD
   * `speech start` sendet (VadConfig.minSpeechMs, Default 250 ms). Nur
   * Listener; ohne Wert gilt der Default der Capture-Lib.
   */
  vadMinSpeechMs?: number;
}

export type SourceInput = 'device' | 'display';

/**
 * Preset im Formular und in der Liste: die drei Arten plus Konferenz
 * (= `kind: room` mit `input: display`).
 */
export type Preset = StoredSource['kind'] | 'conference';

export interface StoredState {
  lastToken: string | null;
  sources: StoredSource[];
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const EMPTY: StoredState = { lastToken: null, sources: [] };

export function loadState(store: KeyValueStore | null): StoredState {
  if (!store) return { ...EMPTY, sources: [] };
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY, sources: [] };
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    const sources = Array.isArray(parsed.sources) ? parsed.sources.map(withDefaultInput).filter(isStoredSource) : [];
    return { lastToken: typeof parsed.lastToken === 'string' ? parsed.lastToken : null, sources };
  } catch {
    return { ...EMPTY, sources: [] };
  }
}

export function saveState(store: KeyValueStore | null, state: StoredState): void {
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* privater Modus / voll — App funktioniert ohne Persistenz weiter */
  }
}

/** Einträge von vor dem Konferenz-Preset haben kein `input` → Mikrofon. */
function withDefaultInput(x: unknown): unknown {
  return typeof x === 'object' && x !== null && !('input' in x) ? { ...x, input: 'device' } : x;
}

function isStoredSource(x: unknown): x is StoredSource {
  const s = x as StoredSource;
  return (
    typeof s === 'object' &&
    s !== null &&
    typeof s.id === 'string' &&
    /^src_[0-9A-Za-z_-]{4,64}$/.test(s.id) &&
    (s.kind === 'lapel' || s.kind === 'room' || s.kind === 'remote') &&
    // unbekannter Eingang oder Tab-Audio an einer Ein-Sprecher-Quelle → verwerfen
    (s.input === 'device' || (s.input === 'display' && s.kind === 'room')) &&
    typeof s.label === 'string' &&
    (s.mode === 'listener' || s.mode === 'ptt')
  );
}

export function newStoredSource(id: string, kind: StoredSource['kind'], label: string): StoredSource {
  return {
    id,
    kind,
    label,
    input: 'device',
    participationId: null,
    deviceId: null,
    deviceLabel: null,
    channel: null,
    channels: null,
    mode: defaultMode(kind),
    processing: defaultProcessing(kind),
  };
}

/**
 * Modus-Default pro Art: Remote startet mit Push-to-Talk, weil ein Laptop-Mikro
 * ohne Headset die anderen Konferenz-Teilnehmer aus dem eigenen Lautsprecher
 * hört und die Browser-AEC dieses Audio nicht kennt (Doc 15 §1.1).
 */
export function defaultMode(kind: StoredSource['kind']): CaptureMode {
  return kind === 'remote' ? 'ptt' : 'listener';
}

/**
 * Art einer Quelle wechseln (Formular): Processing nach Art-Default; Remote
 * erzwingt beim Wechsel PTT (danach frei umschaltbar), Lapel/Raum lassen den
 * Modus wie bisher stehen; Raum bekommt ohne Namen „Raum". Eingang immer
 * Mikrofon (Tab-Audio nur über `withPreset(s, 'conference')`).
 */
export function withKind(s: StoredSource, kind: StoredSource['kind']): StoredSource {
  return {
    ...s,
    kind,
    input: 'device',
    processing: defaultProcessing(kind),
    mode: kind === 'remote' ? 'ptt' : s.mode,
    label: kind === 'room' && !s.label ? 'Raum' : s.label,
  };
}

const CONFERENCE_LABEL = 'Konferenz';

/**
 * Preset wählen (Radio „Art"). Konferenz = Raum (Diarisierung, Processing aus)
 * mit Tab-Audio als Eingang, immer Dauer-Listener, ohne Gerät/Kanal; ohne
 * eigenen Namen „Konferenz". Beim Verlassen von Konferenz fällt der
 * Vorgabename weg, damit Raum wieder „Raum" bekommt.
 */
export function withPreset(s: StoredSource, preset: Preset): StoredSource {
  if (preset === 'conference') {
    return {
      ...withKind(s, 'room'),
      input: 'display',
      mode: 'listener',
      label: !s.label || s.label === 'Raum' ? CONFERENCE_LABEL : s.label,
      deviceId: null,
      deviceLabel: null,
      channel: null,
      channels: null,
    };
  }
  const from = presetOf(s) === 'conference' && s.label === CONFERENCE_LABEL ? { ...s, label: '' } : s;
  return withKind(from, preset);
}

/** Preset einer Quelle (Konferenz = Raum mit Tab-Audio). */
export function presetOf(s: Pick<StoredSource, 'kind' | 'input'>): Preset {
  return s.kind === 'room' && s.input === 'display' ? 'conference' : s.kind;
}

/** Art für die Kartenzeile („Ansteckmikro · Push-to-Talk"). */
export function kindLabel(preset: Preset): string {
  switch (preset) {
    case 'room':
      return 'Raum';
    case 'remote':
      return 'Remote';
    case 'conference':
      return 'Konferenz';
    default:
      return 'Ansteckmikro';
  }
}

/** Kurztext des Badges in der Quellenliste (Klasse `badge <preset>`, Konferenz → `badge conference`). */
export function kindBadge(preset: Preset): string {
  return preset === 'lapel' ? 'Lapel' : kindLabel(preset);
}

/** Zweite Kartenzeile: Gerät und Kanal bzw. Tab-Audio. */
export function inputLabel(s: StoredSource, hasLiveStream: boolean): string {
  if (s.input === 'display') return hasLiveStream ? 'Tab-Audio geteilt' : 'Tab-Audio · kein Tab gewählt';
  return `${s.deviceLabel ?? 'Standard-Mikrofon'} · ${channelLabel(s.channel, s.channels)}`;
}

/** Texte rund um Tab-Audio (Doc 15 §1.2 und §2). */
export const DISPLAY_TEXT = {
  shareAudio: 'Teams/Zoom im Browser-Tab öffnen, beim Teilen „Audio teilen" anhaken.',
  onePath: 'Pro Person nur ein Weg: wer den Join-Link nutzt, darf nicht zusätzlich über den Konferenz-Feed ankommen.',
  noAudio: 'Kein Audio im geteilten Inhalt. Beim Teilen „Audio teilen" anhaken.',
  ended: 'Teilen beendet. Tab erneut wählen, um weiterzumachen.',
  notPicked: 'Kein Tab gewählt. Erst Tab oder Fenster wählen.',
  unsupported: 'In diesem Browser nicht verfügbar (Safari, Smartphone, Tablet). Chrome oder Edge am Laptop verwenden.',
} as const;

/** Beschriftung des Wähl-Buttons: erstmals, nach Reload/Teilen-Ende, oder Wechsel. */
export function displayPickLabel(hasLiveStream: boolean, pickedBefore: boolean): string {
  if (hasLiveStream) return 'Anderen Tab wählen';
  return pickedBefore ? 'Tab erneut wählen' : 'Tab oder Fenster wählen';
}

/**
 * Kann der Browser Tab-Audio teilen? Ohne `getDisplayMedia` (Smartphone,
 * Tablet, ältere Browser) nicht; Safari kennt `getDisplayMedia`, liefert aber
 * kein Audio (Doc 15 §1.2, Tabelle).
 */
export function displayAudioSupported(env: { getDisplayMedia?: unknown; userAgent: string }): boolean {
  if (typeof env.getDisplayMedia !== 'function') return false;
  const ua = env.userAgent;
  const safari = /Safari\//.test(ua) && !/(Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS)\//.test(ua);
  return !safari;
}

/** Hinweis unter der Modus-Wahl; nur für Remote (Übersprechen ohne Headset, Doc 15 §1.1). */
export function modeHint(s: Pick<StoredSource, 'kind' | 'mode'>): string | null {
  if (s.kind !== 'remote') return null;
  return s.mode === 'listener'
    ? 'Mit Headset ok, ohne Headset werden andere Teilnehmer als deine Aussage erkannt.'
    : 'Mit Headset kannst du auf Dauer-Listener umschalten.';
}

/** Gespeicherte Quelle → `source` der start-Nachricht (Doc 13 §2.1). */
export function toSourceConfig(s: StoredSource, language: string): SourceConfig {
  const cfg: SourceConfig = {
    id: s.id,
    kind: s.kind,
    label: s.label,
    speaker: s.kind === 'room' ? null : { label: s.label, participationId: s.participationId },
    mode: s.mode,
    diarize: s.kind === 'room',
    language,
    processing: s.processing,
  };
  if (s.input === 'display') {
    // Tab-Audio wird im Worklet gemischt (Downmix, keine Kanalwahl); für Gateway/Regie nur informativ.
    cfg.device = { label: 'Konferenz-Feed' };
  } else if (s.deviceLabel || s.channel !== null) {
    cfg.device = {};
    if (s.deviceLabel) cfg.device.label = s.deviceLabel;
    if (s.channel !== null) cfg.device.channel = s.channel;
    if (s.channels !== null) cfg.device.channels = s.channels;
  }
  return cfg;
}

/** VAD-Einstellungen einer Quelle für `VoiceCapture({ vad })`; ungültige Werte werden ignoriert. */
export function vadOverrides(s: StoredSource): { minSpeechMs?: number } {
  const v = s.vadMinSpeechMs;
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 2000 ? { minSpeechMs: Math.round(v) } : {};
}

/** Token aus `/join/<token>` (nie aus der Query). */
export function tokenFromPath(pathname: string): string | null {
  const m = /^\/join\/([A-Za-z2-7]{8}-[A-Za-z2-7]{16})\/?$/.exec(pathname);
  return m ? (m[1] as string).toUpperCase() : null;
}

/** ws(s)-Basis-URL aus der Seiten-Herkunft. */
export function wsBase(origin: string): string {
  return origin.replace(/^http/, 'ws');
}

export function channelLabel(channel: number | null, channels: number | null): string {
  if (channel === null) return channels && channels > 1 ? 'Mono (Mischung)' : 'Mono';
  if (channels === 2) return channel === 0 ? 'Kanal 1 (links)' : 'Kanal 2 (rechts)';
  return `Kanal ${channel + 1}`;
}

// ------------------------------------------------------------------ Embed-Modus (Regie-Browser)

/**
 * Optionen aus der Query der Join-URL, wenn die Regie die App als iframe
 * einbettet (`<iframe src="<joinUrl>?embed=1&preset=room" allow="microphone; display-capture">`):
 *  - `embed=1` (oder `true`): kompakte Darstellung + postMessage an `window.parent`
 *  - `preset=lapel|remote|room|conference`: ohne gespeicherte Quellen beim ersten
 *    Laden direkt das „Neue Quelle"-Formular mit dieser Art
 *  - `name=…`: Vorgabe für den Namen in diesem Formular (getrimmt, ≤ 80 Zeichen)
 * Unbekannte/ungültige Werte werden ignoriert.
 */
export interface EmbedOptions {
  embed: boolean;
  preset: Preset | null;
  name: string | null;
}

const PRESETS: readonly Preset[] = ['lapel', 'remote', 'room', 'conference'];

export function parseEmbedOptions(search: string): EmbedOptions {
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(search);
  } catch {
    return { embed: false, preset: null, name: null };
  }
  const flag = (q.get('embed') ?? '').trim().toLowerCase();
  const p = (q.get('preset') ?? '').trim().toLowerCase();
  const name = (q.get('name') ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
    .trim();
  return {
    embed: flag === '1' || flag === 'true',
    preset: (PRESETS as readonly string[]).includes(p) ? (p as Preset) : null,
    name: name || null,
  };
}

/**
 * Entwurf für das „Neue Quelle"-Formular beim ersten Laden: nur mit `preset`
 * und nur, wenn das Gerät noch keine Quellen gespeichert hat.
 */
export function initialDraft(opts: EmbedOptions, storedSources: number, id: string): StoredSource | null {
  if (!opts.preset || storedSources > 0) return null;
  return withPreset(newStoredSource(id, 'lapel', opts.name ?? ''), opts.preset);
}

/**
 * Ziel-Origin für postMessage = Origin aus `document.referrer` (die Regie-Seite,
 * die das iframe geladen hat). Kein/ungültiger/nicht-http(s)-Referrer → null:
 * dann wird nicht gepostet (nie `'*'`).
 */
export function parentOrigin(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const u = new URL(referrer);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.origin : null;
  } catch {
    return null;
  }
}

export type EmbedEvent = 'ready' | 'started' | 'stopped' | 'error';

/** Nachricht an die einbettende Regie — bewusst nur diese Felder (keine Namen, keine Transkripte). */
export interface EmbedMessage {
  type: 'voice-join';
  event: EmbedEvent;
  sourceId?: string;
  /** Art der Quelle wie in `preset` (Konferenz = `conference`) */
  kind?: Preset;
  /** nur bei `error`: Fehlertext (≤ 300 Zeichen) */
  message?: string;
}

export function embedMessage(event: EmbedEvent, extra: { sourceId?: string; kind?: Preset; message?: string | null } = {}): EmbedMessage {
  const m: EmbedMessage = { type: 'voice-join', event };
  if (extra.sourceId) m.sourceId = extra.sourceId;
  if (extra.kind) m.kind = extra.kind;
  if (event === 'error' && extra.message) m.message = extra.message.slice(0, 300);
  return m;
}
